import { type ReactElement } from "react";
import omit from "lodash/omit";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

interface IModalState {
  open: boolean;
}

interface IModalOptions {
  onClose?(): void;
  /**
   * Important will disable ESC key and clicking outside of the modal to close it
   */
  important?: boolean;
  /**
   * If true, the X button will be hidden
   */
  hideCloseButton?: boolean;
  /**
   * If true, the modal will be rendered as a drawer on mobile devices
   */
  asDrawer?: boolean;
  /**
   * Custom modal close function.
   */
  onCustomClose?(): void;
}

export interface IUseModalProps extends IModalOptions {
  id: number;
}

interface IModalStore {
  idGenerator: number;
  state: Record<number, IModalState>;
  modals: Record<number, React.FC>;
  create: (
    modal: (props: IUseModalProps) => ReactElement,
    options?: IModalOptions,
  ) => number;
  close: (id: number) => void;
  getId: () => number;
  getState: (id: number) => IModalState | undefined;
  remove: (id: number) => void;
  clear: () => void;
}

export const useModalStore = create<IModalStore>((set, get) => ({
  idGenerator: 0,
  state: {},
  modals: {},
  getId: () => {
    const id = get().idGenerator + 1;
    set(() => ({
      idGenerator: id,
    }));
    return id;
  },
  create: (modal, options) => {
    const id = get().getId();
    set((state) => ({
      state: {
        ...state.state,
        [id]: { open: true },
      },
      modals: {
        ...state.modals,
        [id]: () => modal({ id, ...options }),
      },
    }));
    return id;
  },
  close: (id) => {
    set((state) => ({
      state: {
        ...state.state,
        [id]: { open: false },
      },
    }));
  },
  getState: (id) => {
    const state = get().state[id];
    return state;
  },
  remove: (id) => {
    set((state) => ({
      state: omit(state.state, id),
      modals: omit(state.modals, id),
    }));
  },
  clear: () => {
    set(() => ({
      state: {},
      modals: {},
    }));
  },
}));

export const useModalActions = () =>
  useModalStore(
    useShallow((s) => ({ create: s.create, remove: s.remove, close: s.close })),
  );
